import React, { useEffect, useMemo, useState } from 'react'
import whatsappApi from '../whatsapp/whatsappApi'
import SessionManager from '../../Utils/SessionManager'
import myToast from '../../Utils/MyToast'
import {
  formatTemplateName,
  formatVariableLabel,
  getTemplateVariables,
} from '../whatsapp/waTemplateUtils'
import CommTemplatePickerDialog from './CommTemplatePickerDialog'
import CommSendConfirmDialog from './CommSendConfirmDialog'

const WA = '#25D366'
const WA_DARK = '#128C7E'

// Created By Cursor
const CommWhatsAppCompose = ({
  service,
  connected = false,
  onStatusChange,
  titleHint,
}) => {
  const [sessionId, setSessionId] = useState('')
  const [templates, setTemplates] = useState([])
  const [templateId, setTemplateId] = useState('')
  const [students, setStudents] = useState([])
  const [selected, setSelected] = useState({})
  const [loadingStudents, setLoadingStudents] = useState(false)
  const [sending, setSending] = useState(false)
  const [filter, setFilter] = useState('')
  const [showTemplateDialog, setShowTemplateDialog] = useState(false)
  const [templateSearch, setTemplateSearch] = useState('')
  const [confirmSend, setConfirmSend] = useState(null)
  const [verifying, setVerifying] = useState(false)

  const serviceName =
    titleHint || service?.service_name || service?.service_code || 'Message'

  const serviceKeys = useMemo(() => {
    const set = new Set()
    const code = String(service?.service_code || '').toLowerCase().trim()
    const group = String(service?.group_name || '').toLowerCase().trim()
    const name = String(service?.service_name || '').toLowerCase().trim()
    if (code) set.add(code)
    if (group) set.add(group)
    if (name) set.add(name)
    return set
  }, [service])

  const visibleTemplates = useMemo(() => {
    if (!serviceKeys.size) return templates
    const filtered = templates.filter((t) => {
      const g = String(t.group_name || '').toLowerCase().trim()
      const code = String(t.service_code || '').toLowerCase().trim()
      const name = String(t.name || '').toLowerCase().trim()
      if (code && serviceKeys.has(code)) return true
      if (g && serviceKeys.has(g)) return true
      // soft match: template name/group contains service code/name
      for (const key of serviceKeys) {
        if (key && (g.includes(key) || name.includes(key) || key.includes(g))) {
          return true
        }
      }
      return false
    })
    return filtered.length ? filtered : templates
  }, [templates, serviceKeys])

  useEffect(() => {
    try {
      const s = SessionManager.getCurrentSession()
      if (s && s[0]) setSessionId(s[0].session_id || s[0].id || '')
    } catch (e) {
      /* ignore */
    }

    whatsappApi.getTemplates().then((res) => {
      if (res.api_status === 'OK') {
        const list = (res.data.templates || []).filter((t) => t.status === 'approved')
        const use = list.length ? list : res.data.templates || []
        setTemplates(use)
      }
    })
  }, [])

  useEffect(() => {
    // Keep selection only if still valid for this service; do not auto-pick
    if (!templateId) return
    const stillValid = visibleTemplates.some((t) => String(t.id) === String(templateId))
    if (!stillValid) setTemplateId('')
  }, [visibleTemplates, templateId])

  const dialogTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase()
    if (!q) return visibleTemplates
    return visibleTemplates.filter((t) =>
      [t.name, t.status, t.category, t.group_name, t.service_code, t.body_text, t.body, t.message]
        .map((v) => String(v || '').toLowerCase())
        .join(' ')
        .includes(q)
    )
  }, [visibleTemplates, templateSearch])

  // Created By Cursor
  const openTemplateDialog = () => {
    setTemplateSearch('')
    setShowTemplateDialog(true)
  }

  // Created By Cursor
  const pickTemplate = (tpl) => {
    if (!tpl?.id) return
    setTemplateId(String(tpl.id))
    setShowTemplateDialog(false)
  }

  // Created By Cursor
  const loadStudents = async () => {
    if (!sessionId) {
      myToast.notify_error('Session not found')
      return
    }
    setLoadingStudents(true)
    try {
      const res = await whatsappApi.getStudentsForMessaging({
        session_id: sessionId,
      })
      if (res.api_status === 'OK') {
        const rows = res.data.students || []
        setStudents(rows)
        const map = {}
        rows.forEach((r) => {
          // Only auto-select numbers that pass WhatsApp format rules
          if (r.contact_number && r.format_valid !== false) map[r.student_id] = true
        })
        setSelected(map)
        setFilter('')
      } else {
        myToast.notify_error(res.api_remark || 'Failed to load students')
      }
    } catch (e) {
      myToast.notify_error('Failed to load students')
    } finally {
      setLoadingStudents(false)
    }
  }

  const selectedList = useMemo(
    () =>
      students.filter(
        (s) => selected[s.student_id] && s.contact_number && s.format_valid !== false
      ),
    [students, selected]
  )

  // Created By Cursor — WhatsApp format badge
  const formatBadge = (s) => {
    if (!s.contact_number) {
      return { label: 'No number', bg: '#f1f5f9', color: '#64748b' }
    }
    if (s.format_valid === false) {
      return { label: 'Bad format', bg: '#fef3c7', color: '#b45309' }
    }
    const st = String(s.whatsapp_status || 'format_ok')
    if (st === 'valid') return { label: 'On WhatsApp', bg: '#dcfce7', color: '#15803d' }
    if (st === 'invalid') return { label: 'Not on WA', bg: '#fee2e2', color: '#b91c1c' }
    if (st === 'invalid_format') return { label: 'Bad format', bg: '#fef3c7', color: '#b45309' }
    return { label: 'Format OK', bg: '#e0f2fe', color: '#0369a1' }
  }

  const visibleStudents = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return students
    return students.filter((s) =>
      [s.student_name, s.student_id, s.contact_number, s.class_id, s.section_id]
        .map((v) => String(v || '').toLowerCase())
        .join(' ')
        .includes(q)
    )
  }, [students, filter])

  // Created By Cursor
  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }))

  // Created By Cursor
  const toggleAllVisible = () => {
    const withPhone = visibleStudents.filter(
      (s) => s.contact_number && s.format_valid !== false
    )
    const allOn = withPhone.length > 0 && withPhone.every((s) => selected[s.student_id])
    setSelected((prev) => {
      const next = { ...prev }
      withPhone.forEach((s) => {
        next[s.student_id] = !allOn
      })
      return next
    })
  }

  // Created By Cursor — Meta / history check for WhatsApp registration
  const verifyWhatsAppContacts = async () => {
    const list = (selectedList.length ? selectedList : students).filter(
      (s) => s.contact_number && s.format_valid !== false
    )
    if (!list.length) {
      myToast.notify_error('Load students with valid phone format first')
      return
    }
    setVerifying(true)
    try {
      const res = await whatsappApi.verifyContacts(
        list.map((s) => ({
          student_id: s.student_id,
          contact_number: s.contact_number,
        })),
        true
      )
      if (res.api_status !== 'OK') {
        myToast.notify_error(res.api_remark || 'Verify failed')
        return
      }
      const byStudent = {}
      const byPhone = {}
      ;(res.data?.contacts || []).forEach((c) => {
        if (c.student_id) byStudent[String(c.student_id)] = c
        if (c.wa_to) byPhone[c.wa_to] = c
        if (c.input) byPhone[String(c.input).replace(/\D+/g, '')] = c
      })
      setStudents((prev) =>
        prev.map((s) => {
          const hit =
            byStudent[String(s.student_id)] ||
            byPhone[s.wa_to] ||
            byPhone[String(s.contact_number || '').replace(/\D+/g, '')]
          if (!hit) return s
          return {
            ...s,
            format_valid: hit.format_valid,
            wa_to: hit.wa_to || s.wa_to,
            e164: hit.e164 || s.e164,
            format_errors: hit.format_errors || s.format_errors,
            whatsapp_status: hit.whatsapp_status || s.whatsapp_status,
            wa_id: hit.wa_id || null,
            verify_message: hit.message || '',
          }
        })
      )
      const valid = (res.data?.contacts || []).filter((c) => c.whatsapp_status === 'valid').length
      const badFmt = (res.data?.contacts || []).filter((c) => !c.format_valid).length
      const unknown = (res.data?.contacts || []).filter((c) => c.whatsapp_status === 'unknown').length
      myToast.notify_success(
        `Checked ${list.length}: ${valid} on WhatsApp, ${badFmt} bad format, ${unknown} unknown`
      )
      if (res.api_remark) {
        // Soft info — Meta Cloud API often cannot confirm presence
        console.info('verifyContacts:', res.api_remark)
      }
    } catch (e) {
      myToast.notify_error(e.message || 'Verify failed')
    } finally {
      setVerifying(false)
    }
  }

  // Created By Cursor
  const sendToQueue = () => {
    if (!connected) {
      myToast.notify_error('Connect WhatsApp first')
      return
    }
    if (!templateId) {
      myToast.notify_error('Select a template')
      return
    }
    if (!selectedList.length) {
      myToast.notify_error('Select at least one student with a valid WhatsApp number format')
      return
    }
    const tpl =
      visibleTemplates.find((t) => String(t.id) === String(templateId)) ||
      templates.find((t) => String(t.id) === String(templateId))

    setConfirmSend({
      count: selectedList.length,
      templateName: tpl?.name || '',
      recipients: selectedList.map((s) => ({
        // Prefer Cloud API format (country code, digits only)
        contact_number: s.wa_to || s.contact_number,
        student_id: s.student_id,
        recipient_type: 'STUDENT',
        message: JSON.stringify([s.student_name || '', s.class_id || '']),
      })),
      template: tpl,
    })
  }

  // Created By Cursor
  const confirmSendToQueue = async () => {
    if (!confirmSend?.recipients?.length) return
    const staffId = SessionManager.getStaffDetails()?.staff_id
    const tpl = confirmSend.template
    setSending(true)
    try {
      const res = await whatsappApi.addToQueue({
        recipients: confirmSend.recipients,
        template_id: Number(templateId),
        template_name: tpl?.name || '',
        message_type: 'template',
        authorized_staff_id: staffId,
        process_now: true,
        service_code: service?.service_code || undefined,
      })
      if (res.api_status === 'OK') {
        const sent = Number(res.data?.processed?.sent || 0)
        const failed = Number(res.data?.processed?.failed || 0)
        const queued = Number(res.data?.queued?.success_count || 0)
        setConfirmSend(null)
        if (failed > 0 && sent === 0) {
          myToast.notify_error(
            queued
              ? `${failed} message${failed === 1 ? '' : 's'} failed to send`
              : 'Send failed'
          )
        } else if (failed > 0) {
          myToast.notify_warning(`Sent ${sent}, ${failed} failed`)
        } else {
          myToast.notify_success(
            sent > 0
              ? `Sent to ${sent} recipient${sent === 1 ? '' : 's'}`
              : `Queued ${queued} message${queued === 1 ? '' : 's'}`
          )
        }
        if (onStatusChange) onStatusChange()
      } else {
        myToast.notify_error(res.api_remark || 'Send failed')
      }
    } catch (e) {
      myToast.notify_error('Send failed')
    } finally {
      setSending(false)
    }
  }

  const selectedTemplate = useMemo(
    () =>
      visibleTemplates.find((t) => String(t.id) === String(templateId)) ||
      templates.find((t) => String(t.id) === String(templateId)) ||
      null,
    [visibleTemplates, templates, templateId]
  )

  const selectedBodyText =
    selectedTemplate?.body_text ||
    selectedTemplate?.body ||
    selectedTemplate?.message ||
    ''
  const selectedCategory = selectedTemplate?.category || ''

  const selectedVariables = useMemo(
    () => getTemplateVariables(selectedTemplate),
    [selectedTemplate]
  )

  const fieldLabel = {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    marginBottom: 4,
  }

  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '10px 12px',
          background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>
          Compose · {serviceName}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
          {selectedList.length} selected
          {students.length ? ` / ${students.length}` : ''}
        </div>
      </div>

      <div style={{ padding: '12px' }}>
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <label style={{ ...fieldLabel, marginBottom: 0 }}>Template</label>
            <button type="button" onClick={openTemplateDialog} style={btnSecondary}>
              <i className="fa fa-file-text-o" style={{ marginRight: 6 }} />
              {selectedTemplate ? 'Change Template' : 'Use Template'}
            </button>
          </div>

          {selectedTemplate ? (
            <div
              style={{
                border: `1px solid ${WA}44`,
                borderRadius: 12,
                background: 'linear-gradient(180deg, #f0fdf4 0%, #fff 48%)',
                padding: '12px 14px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: selectedBodyText ? 10 : 0,
                }}
              >
                <div style={{ flex: '1 1 180px', minWidth: 140 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 15,
                      color: '#0f172a',
                      lineHeight: 1.3,
                    }}
                  >
                    {formatTemplateName(selectedTemplate.name)}
                  </div>
                </div>
                {selectedCategory ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                      color: '#0f766e',
                      background: '#ccfbf1',
                      borderRadius: 999,
                      padding: '5px 10px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedCategory}
                  </span>
                ) : null}
                {selectedTemplate.status ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      color:
                        selectedTemplate.status === 'approved'
                          ? WA_DARK
                          : selectedTemplate.status === 'pending'
                          ? '#b45309'
                          : '#64748b',
                      background:
                        selectedTemplate.status === 'approved'
                          ? `${WA}18`
                          : selectedTemplate.status === 'pending'
                          ? '#fef3c7'
                          : '#f1f5f9',
                      borderRadius: 999,
                      padding: '5px 10px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedTemplate.status}
                  </span>
                ) : null}
              </div>

              {selectedVariables.length > 0 && (
                <div style={{ marginBottom: selectedBodyText ? 10 : 0 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: '#94a3b8',
                      marginBottom: 6,
                    }}
                  >
                    Used variables
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {selectedVariables.map((v) => (
                      <span
                        key={`${v.placeholder}-${v.label || v.index}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#334155',
                          background: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          padding: '5px 10px',
                        }}
                      >
                        <code
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: WA_DARK,
                            background: `${WA}14`,
                            borderRadius: 4,
                            padding: '1px 5px',
                          }}
                        >
                          {v.placeholder}
                        </code>
                        {v.label ? (
                          <span style={{ color: '#64748b' }}>
                            {formatVariableLabel(v.label)}
                          </span>
                        ) : (
                          <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>
                            Variable {v.index}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedBodyText ? (
                <div
                  style={{
                    fontSize: 13,
                    color: '#334155',
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}
                >
                  {selectedBodyText}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
                  No body text on this template
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                border: '1px dashed #cbd5e1',
                borderRadius: 12,
                background: '#f8fafc',
                padding: '16px 14px',
                color: '#94a3b8',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              No template selected — click Use Template to choose one
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
            marginBottom: students.length ? 10 : 0,
          }}
        >
          <button
            type="button"
            onClick={loadStudents}
            disabled={loadingStudents}
            style={btnSecondary}
          >
            {loadingStudents ? 'Loading…' : 'Load students'}
          </button>
          <button
            type="button"
            onClick={verifyWhatsAppContacts}
            disabled={verifying || !students.length}
            style={{
              ...btnSecondary,
              opacity: verifying || !students.length ? 0.65 : 1,
            }}
            title="Check number format and whether contact is on WhatsApp"
          >
            {verifying ? 'Verifying…' : 'Verify WhatsApp'}
          </button>
          <button
            type="button"
            disabled={sending || !connected}
            onClick={sendToQueue}
            style={{
              ...btnPrimary,
              opacity: sending || !connected ? 0.65 : 1,
            }}
          >
            <i className="fa fa-whatsapp" style={{ marginRight: 6 }} />
            {sending ? 'Sending…' : `Send (${selectedList.length})`}
          </button>
          {!connected && (
            <span style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>
              Connect WhatsApp in Settings first
            </span>
          )}
        </div>

        {students.length > 0 && (
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 8,
                alignItems: 'center',
              }}
            >
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Filter students…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{ maxWidth: 240, borderRadius: 8 }}
              />
              <button type="button" onClick={toggleAllVisible} style={btnGhost}>
                Toggle visible
              </button>
            </div>

            <div
              className="table-responsive"
              style={{
                maxHeight: 300,
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
              }}
            >
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 36, padding: '8px 10px' }} />
                    <th style={{ padding: '8px 10px' }}>Student</th>
                    <th style={{ padding: '8px 10px', width: 90 }}>Class</th>
                    <th style={{ padding: '8px 10px', width: 130 }}>Contact</th>
                    <th style={{ padding: '8px 10px', width: 110 }}>WhatsApp</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleStudents.map((s) => {
                    const canSelect = !!s.contact_number && s.format_valid !== false
                    const badge = formatBadge(s)
                    return (
                      <tr
                        key={s.student_id}
                        style={{
                          opacity: canSelect ? 1 : 0.45,
                          background: selected[s.student_id] ? `${WA}0f` : undefined,
                        }}
                        title={
                          (s.format_errors && s.format_errors.length
                            ? s.format_errors.join('; ')
                            : '') ||
                          s.verify_message ||
                          (s.wa_to ? `Send as ${s.wa_to}` : '')
                        }
                      >
                        <td style={{ padding: '6px 10px' }}>
                          <input
                            type="checkbox"
                            disabled={!canSelect}
                            checked={!!selected[s.student_id]}
                            onChange={() => toggle(s.student_id)}
                          />
                        </td>
                        <td style={{ padding: '6px 10px', fontWeight: 600 }}>
                          {s.student_name}
                        </td>
                        <td style={{ padding: '6px 10px', color: '#64748b' }}>
                          {s.class_id}
                          {s.section_id ? `-${s.section_id}` : ''}
                        </td>
                        <td style={{ padding: '6px 10px', fontVariantNumeric: 'tabular-nums' }}>
                          {s.contact_number || '—'}
                          {s.wa_to && s.wa_to !== String(s.contact_number || '').replace(/\D+/g, '') ? (
                            <div style={{ fontSize: 11, color: '#64748b' }}>+{s.wa_to}</div>
                          ) : null}
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 700,
                              background: badge.bg,
                              color: badge.color,
                            }}
                          >
                            {badge.label}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                  {visibleStudents.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ padding: 14, textAlign: 'center', color: '#94a3b8' }}>
                        No matching students
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showTemplateDialog && (
        <CommTemplatePickerDialog
          serviceName={serviceName}
          templates={dialogTemplates}
          selectedId={templateId}
          search={templateSearch}
          onSearch={setTemplateSearch}
          onPick={pickTemplate}
          onClose={() => setShowTemplateDialog(false)}
        />
      )}

      {confirmSend ? (
        <CommSendConfirmDialog
          title={`Send ${serviceName}?`}
          subtitle="Messages will be queued and sent on WhatsApp"
          recipientCount={confirmSend.count}
          templateName={confirmSend.templateName}
          confirmLabel="Send now"
          busy={sending}
          onConfirm={confirmSendToQueue}
          onClose={() => {
            if (!sending) setConfirmSend(null)
          }}
        />
      ) : null}
    </div>
  )
}

const btnPrimary = {
  border: 'none',
  background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
  color: '#fff',
  fontWeight: 700,
  fontSize: 13,
  padding: '7px 14px',
  borderRadius: 8,
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(37, 211, 102, 0.28)',
}

const btnSecondary = {
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#334155',
  fontWeight: 700,
  fontSize: 13,
  padding: '7px 12px',
  borderRadius: 8,
  cursor: 'pointer',
}

const btnGhost = {
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  color: '#475569',
  fontWeight: 600,
  fontSize: 12,
  padding: '5px 10px',
  borderRadius: 8,
  cursor: 'pointer',
}

export default CommWhatsAppCompose
